// src/pages/RGPD/ConditionsUtilisationPage.jsx
import React, { useEffect, useState } from "react";
import {
  FaBalanceScale,
  FaBan,
  FaCheckCircle,
  FaClipboardList,
  FaDatabase,
  FaEnvelope,
  FaExclamationTriangle,
  FaGavel,
  FaIdCard,
  FaLock,
  FaMedal,
  FaRocket,
  FaShieldAlt,
  FaTrophy,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";

import useScrollToTop from "../../hooks/useScrollToTop";
import { getSettings } from "../../services/settingsServices";

const APP_LOGO = "/logo_monapplitournoi_512.png";

const DEFAULT_SETTINGS = {
  siteweb_club: "",
  nom_club: "",
  adresse_club: "",
  responsable_club: "",
  mail_club: "",
  tel_club: "",
  siret_club: "",
};

const fallback = (value, defaultValue = "Non renseigné") => {
  const cleaned = String(value || "").trim();
  return cleaned || defaultValue;
};

const InfoCard = ({ icon: Icon, title, children }) => {
  return (
    <article className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300">
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0">
          <h2 className="text-lg font-bold text-slate-950 dark:text-white">
            {title}
          </h2>

          <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
            {children}
          </div>
        </div>
      </div>
    </article>
  );
};

const Badge = ({ children }) => {
  return (
    <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-200">
      {children}
    </span>
  );
};

const ConditionsUtilisationPage = () => {
  useScrollToTop();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    let cancelled = false;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();

        if (!cancelled && Array.isArray(data) && data.length > 0) {
          setSettings({
            ...DEFAULT_SETTINGS,
            ...data[0],
          });
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);
      }
    };

    fetchSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  const currentYear = new Date().getFullYear();
  const siteUrl = fallback(settings.siteweb_club, "MonAppliTournoi");
  const contactEmail = fallback(settings.mail_club, "monappliclub@gmail.com");

  return (
    <main className="min-h-screen bg-slate-50 pt-[120px] text-slate-950 dark:bg-slate-950 dark:text-white">
      <section className="relative overflow-hidden px-4 pb-16 sm:px-6 lg:px-8">
        <div className="pointer-events-none absolute -left-24 top-20 h-80 w-80 rounded-full bg-blue-400/15 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 top-80 h-80 w-80 rounded-full bg-orange-400/15 blur-3xl" />

        <div className="relative mx-auto max-w-[1180px]">
          <header className="overflow-hidden rounded-[2.25rem] bg-slate-950 text-white shadow-2xl shadow-slate-950/20">
            <div className="relative p-6 sm:p-8 lg:p-10">
              <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-orange-500/25 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-24 -right-20 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />

              <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <div className="max-w-3xl">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-200">
                    <FaGavel className="h-3.5 w-3.5" />
                    Textes réglementaires
                  </div>

                  <h1 className="mt-5 text-3xl font-bold tracking-tight sm:text-5xl">
                    Conditions d’utilisation
                  </h1>

                  <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-300 sm:text-base">
                    Les présentes conditions encadrent l’accès et l’utilisation
                    de MonAppliTournoi, application web dédiée à la gestion de
                    tournois interclubs de Judo / Jujitsu : création de
                    compétitions, import des combattants, pesées, répartitions,
                    tables d’arbitrage, scoreboards et résultats publics.
                  </p>
                </div>

                <div className="flex justify-center lg:justify-end">
                  <div className="rounded-[2rem] border border-white/10 bg-white/10 p-4 backdrop-blur">
                    <img
                      src={APP_LOGO}
                      alt="Logo MonAppliTournoi"
                      className="h-28 w-28 rounded-[1.75rem] object-contain sm:h-32 sm:w-32"
                    />
                  </div>
                </div>
              </div>
            </div>
          </header>

          <div className="mt-8 grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
            <InfoCard icon={FaIdCard} title="1. Identification du service">
              <p>
                Le site et l’application <strong>MonAppliTournoi</strong> sont
                accessibles à l’adresse suivante : <strong>{siteUrl}</strong>.
              </p>

              <ul className="space-y-2">
                <li>
                  <strong>Nom du service :</strong> MonAppliTournoi
                </li>
                <li>
                  <strong>Responsable de publication :</strong>{" "}
                  {fallback(settings.responsable_club, "Laurent VICTORIN")}
                </li>
                <li>
                  <strong>Contact :</strong>{" "}
                  <a
                    href={`mailto:${contactEmail}`}
                    className="font-bold text-orange-600 underline underline-offset-4 dark:text-orange-300"
                  >
                    {contactEmail}
                  </a>
                </li>
                <li>
                  <strong>Téléphone :</strong>{" "}
                  {fallback(settings.tel_club, "06 80 72 77 07")}
                </li>
                <li>
                  <strong>SIRET :</strong>{" "}
                  {fallback(settings.siret_club, "À compléter si applicable")}
                </li>
              </ul>
            </InfoCard>

            <InfoCard icon={FaCheckCircle} title="2. Acceptation des conditions">
              <p>
                L’accès au site, la création d’un compte, l’utilisation d’une
                démonstration, l’achat d’une formule ou l’utilisation d’une
                compétition implique l’acceptation des présentes conditions.
              </p>

              <p>
                Si l’utilisateur n’accepte pas ces conditions, il doit cesser
                d’utiliser l’application et ne pas créer ou administrer de
                compétition via MonAppliTournoi.
              </p>

              <p>
                L’éditeur se réserve le droit de modifier les présentes
                conditions afin de tenir compte des évolutions techniques,
                fonctionnelles, réglementaires ou commerciales du service.
              </p>
            </InfoCard>
          </div>

          <div className="mt-5 grid gap-5">
            <InfoCard icon={FaRocket} title="3. Description du service">
              <p>
                MonAppliTournoi est une application web permettant d’organiser
                plus facilement des tournois interclubs et compétitions de Judo /
                Jujitsu.
              </p>

              <div className="flex flex-wrap gap-2">
                <Badge>Création de compétitions</Badge>
                <Badge>Paramétrages sportifs</Badge>
                <Badge>Import Excel</Badge>
                <Badge>Formulaire clubs invités</Badge>
                <Badge>Pesées & présences</Badge>
                <Badge>Poules de 2 à 6</Badge>
                <Badge>Tableaux 8 à 64</Badge>
                <Badge>Repêchage systématique</Badge>
                <Badge>Affectation sur tapis</Badge>
                <Badge>Scoreboards</Badge>
                <Badge>Résultats publics</Badge>
                <Badge>Statistiques</Badge>
              </div>

              <p>
                L’application est un outil d’aide à l’organisation. Elle ne se
                substitue pas aux règlements sportifs applicables, aux décisions
                des arbitres, aux consignes de l’organisateur ou aux obligations
                propres à chaque fédération ou événement.
              </p>
            </InfoCard>

            <InfoCard icon={FaUsers} title="4. Utilisateurs et rôles">
              <p>
                L’application peut être utilisée par différents profils selon les
                droits attribués :
              </p>

              <ul className="list-disc space-y-2 pl-5">
                <li>
                  <strong>visiteurs :</strong> consultation de certaines pages
                  publiques ;
                </li>
                <li>
                  <strong>utilisateurs de démonstration :</strong> découverte du
                  fonctionnement avec des données fictives ;
                </li>
                <li>
                  <strong>organisateurs / clients :</strong> création et gestion
                  de compétitions ;
                </li>
                <li>
                  <strong>tables d’arbitrage :</strong> suivi des combats et
                  saisie des résultats selon les accès accordés ;
                </li>
                <li>
                  <strong>administrateurs :</strong> gestion technique,
                  supervision et assistance.
                </li>
              </ul>

              <p>
                Chaque utilisateur s’engage à utiliser uniquement les espaces et
                données auxquels il est autorisé à accéder.
              </p>
            </InfoCard>

            <InfoCard icon={FaLock} title="5. Compte utilisateur et sécurité">
              <p>
                L’utilisateur est responsable de la confidentialité de ses
                identifiants de connexion. Il s’engage à ne pas partager son mot
                de passe et à informer l’éditeur en cas de suspicion d’accès non
                autorisé.
              </p>

              <p>
                L’éditeur peut suspendre ou limiter l’accès à un compte en cas
                d’utilisation abusive, de tentative d’intrusion, de non-respect
                des présentes conditions ou de comportement susceptible de nuire
                au bon fonctionnement du service.
              </p>

              <p>
                L’utilisateur doit s’assurer que les informations saisies dans
                l’application sont exactes, à jour et collectées de manière
                licite.
              </p>
            </InfoCard>

            <InfoCard icon={FaDatabase} title="6. Données de compétition">
              <p>
                L’organisateur est responsable des données qu’il importe ou saisit
                dans l’application : clubs, compétiteurs, catégories, poids,
                résultats, classements, podiums et informations liées au
                déroulement sportif.
              </p>

              <p>
                Lorsque des données de mineurs sont utilisées, l’organisateur
                doit veiller à ne collecter que les informations nécessaires à la
                compétition et à respecter les règles applicables en matière de
                protection des données.
              </p>

              <p>
                Les résultats sportifs peuvent être affichés publiquement lorsque
                la fonctionnalité de résultats publics est activée. Il appartient
                à l’organisateur de vérifier ce qui est publié et de désactiver
                les informations qu’il ne souhaite pas rendre accessibles.
              </p>
            </InfoCard>

            <InfoCard
              icon={FaClipboardList}
              title="7. Import Excel et formulaire clubs invités"
            >
              <p>
                MonAppliTournoi peut permettre l’import de combattants via un
                fichier Excel, une saisie manuelle ou un formulaire transmis aux
                clubs invités.
              </p>

              <p>
                L’utilisateur qui importe ou transmet des informations garantit
                qu’il dispose des droits nécessaires pour utiliser ces données
                dans le cadre de la compétition.
              </p>

              <p>
                Il est recommandé de vérifier soigneusement les fichiers importés
                avant de lancer les pesées, répartitions, poules ou tableaux.
              </p>
            </InfoCard>

            <InfoCard icon={FaTrophy} title="8. Poules, tableaux, tapis et scoreboards">
              <p>
                L’application propose des outils de répartition sportive :
                poules, tableaux, élimination directe, repêchage systématique,
                affectation des distributions sur les tapis et suivi des combats.
              </p>

              <p>
                Malgré les contrôles prévus dans l’interface, l’organisateur doit
                vérifier les répartitions générées, l’ordre des combats,
                l’affectation des tapis et les règles sportives appliquées.
              </p>

              <p>
                Les scoreboards et affichages publics sont des outils de suivi.
                En cas d’écart entre l’affichage numérique, la décision
                arbitrale ou les documents officiels de compétition, les règles
                définies par l’organisateur et les officiels prévalent.
              </p>
            </InfoCard>

            <InfoCard icon={FaMedal} title="9. Résultats publics et statistiques">
              <p>
                Les résultats publics peuvent être affichés en temps réel pour les
                coachs, clubs, familles et visiteurs. Cette publication peut
                inclure les noms des compétiteurs, clubs, catégories,
                classements, podiums et résultats de combat.
              </p>

              <p>
                L’organisateur reste responsable de l’activation, du contrôle et
                de la cohérence des résultats rendus publics.
              </p>

              <p>
                Des statistiques peuvent être produites afin de suivre
                l’avancement de la compétition, les classements par clubs, le
                nombre de combats, les podiums ou les données de fréquentation.
              </p>
            </InfoCard>

            <InfoCard icon={FaShieldAlt} title="10. Données personnelles et RGPD">
              <p>
                Les données personnelles traitées dans MonAppliTournoi sont
                décrites dans la page “Mentions légales & information RGPD” ou
                dans la politique de confidentialité associée au service.
              </p>

              <p>
                Les utilisateurs disposent notamment de droits d’accès, de
                rectification, d’effacement, de limitation, d’opposition et, dans
                certains cas, de portabilité sur leurs données.
              </p>

              <p>
                Toute demande relative aux données personnelles peut être adressée
                à :{" "}
                <a
                  href={`mailto:${contactEmail}`}
                  className="font-bold text-orange-600 underline underline-offset-4 dark:text-orange-300"
                >
                  {contactEmail}
                </a>
                .
              </p>
            </InfoCard>

            <InfoCard icon={FaBan} title="11. Usages interdits">
              <p>Il est interdit d’utiliser MonAppliTournoi pour :</p>

              <ul className="list-disc space-y-2 pl-5">
                <li>porter atteinte à la sécurité de l’application ;</li>
                <li>accéder à des données sans autorisation ;</li>
                <li>usurper l’identité d’un autre utilisateur ;</li>
                <li>importer volontairement des données fausses ou illicites ;</li>
                <li>
                  publier des contenus injurieux, discriminatoires ou illicites ;
                </li>
                <li>perturber le fonctionnement du service ou de ses serveurs ;</li>
                <li>
                  copier, extraire ou réutiliser l’application sans autorisation.
                </li>
              </ul>

              <p>
                En cas d’abus, l’éditeur peut supprimer, suspendre ou limiter
                l’accès au service.
              </p>
            </InfoCard>

            <InfoCard icon={FaExclamationTriangle} title="12. Disponibilité du service">
              <p>
                L’éditeur s’efforce de maintenir le service accessible et
                fonctionnel. Toutefois, l’application peut être temporairement
                indisponible pour maintenance, mise à jour, incident technique,
                surcharge, problème réseau ou interruption d’un prestataire.
              </p>

              <p>
                L’éditeur ne garantit pas une disponibilité permanente et ne
                saurait être tenu responsable des conséquences d’une
                indisponibilité temporaire du service, notamment lors d’une
                compétition, sauf disposition contractuelle spécifique.
              </p>
            </InfoCard>

            <InfoCard icon={FaUserCheck} title="13. Responsabilité de l’organisateur">
              <p>
                L’organisateur de la compétition reste responsable de la bonne
                préparation de son événement, du contrôle des données importées,
                du respect des règlements sportifs, de la vérification des
                résultats et de la communication auprès des clubs participants.
              </p>

              <p>
                MonAppliTournoi constitue un outil d’assistance à
                l’organisation. L’utilisation de l’application ne dispense pas
                l’organisateur de ses responsabilités administratives, sportives,
                réglementaires et de sécurité.
              </p>
            </InfoCard>

            <InfoCard icon={FaBalanceScale} title="14. Limitation de responsabilité">
              <p>
                L’éditeur ne peut être tenu responsable des erreurs de saisie, de
                fichier importé, de mauvais paramétrage, de perte de connexion,
                de mauvaise utilisation, de décision arbitrale, de modification
                manuelle incorrecte ou de publication de résultats non vérifiés.
              </p>

              <p>
                L’utilisateur reconnaît que les outils informatiques nécessitent
                une vérification humaine, en particulier dans le cadre d’une
                compétition sportive.
              </p>
            </InfoCard>

            <InfoCard icon={FaGavel} title="15. Propriété intellectuelle">
              <p>
                Les éléments de MonAppliTournoi, notamment le nom, le logo,
                l’interface, le code, les textes, les composants graphiques, les
                bases de données, les fonctionnalités et la documentation, sont
                protégés par les règles de propriété intellectuelle.
              </p>

              <p>
                Toute reproduction, adaptation, extraction, diffusion,
                modification ou réutilisation non autorisée est interdite.
              </p>
            </InfoCard>

            <InfoCard icon={FaBalanceScale} title="16. Droit applicable">
              <p>
                Les présentes conditions sont soumises au droit français. En cas
                de difficulté, les parties rechercheront prioritairement une
                solution amiable.
              </p>

              <p>
                À défaut d’accord amiable, le litige pourra être porté devant les
                juridictions compétentes, sous réserve des règles impératives
                applicables.
              </p>
            </InfoCard>
          </div>

          <footer className="mt-8 rounded-[2rem] border border-slate-200 bg-white p-5 text-sm text-slate-600 shadow-sm dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300">
            <p>
              Dernière mise à jour : {currentYear}. Ces conditions constituent
              une base générale pour l’utilisation de MonAppliTournoi. Elles
              peuvent être complétées par des conditions commerciales, une
              politique de confidentialité ou des règles particulières selon la
              formule souscrite.
            </p>
          </footer>
        </div>
      </section>
    </main>
  );
};

export default ConditionsUtilisationPage;